import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Input } from '@/components/ui';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, Customer, PagedResponse, PointRewardQuote } from '@/services/types';
import { API_ENDPOINTS, DEBOUNCE_DELAY } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { refreshCalculation } from './cartSlice';

interface Props {
    shopId: number;
    name: string;
    phone: string;
    onChange: (next: { name: string; phone: string }) => void;
    required?: boolean;
    /** Landscape panel: user / phone icons instead of labels */
    icons?: boolean;
}

/**
 * Customer name/phone inputs backed by a native <datalist> fed from customers/search.
 * Picking a suggestion fills both fields.
 */
export const CustomerAutocomplete: React.FC<Props> = ({ shopId, name, phone, onChange, required, icons }) => {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Customer[]>([]);
    const listId = useMemo(() => `customers-${shopId}`, [shopId]);
    const dispatch = useAppDispatch();
    const pointRewardEnabled = useAppSelector((s) => !!s.cart.calculation?.pointRewardEnabled);
    // null = not a member (404); undefined = not looked up
    const [member, setMember] = useState<PointRewardQuote | null | undefined>();

    const lookupMember = async () => {
        if (!pointRewardEnabled || !phone.trim()) { setMember(undefined); return; }
        try {
            const res = await api.get<ApiResponse<PointRewardQuote>>(API_ENDPOINTS.POINT_REWARD_MEMBER(shopId), { params: { phone } });
            setMember(res.data.data);
        } catch { setMember(null); }
    };

    const registerMember = async () => {
        try {
            const res = await api.post<ApiResponse<PointRewardQuote>>(API_ENDPOINTS.POINT_REWARD_MEMBER(shopId), { name, phone });
            setMember(res.data.data);
            dispatch(refreshCalculation());
        } catch (err) { toast.error(getErrorMessage(err)); }
    };

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return; }
        const timer = setTimeout(async () => {
            try {
                const res = await api.get<ApiResponse<PagedResponse<Customer>>>(API_ENDPOINTS.CUSTOMER_SEARCH(shopId), {
                    params: { q, page: 0, size: 10 },
                });
                setResults(res.data.data.content || []);
            } catch { setResults([]); }
        }, DEBOUNCE_DELAY);
        return () => clearTimeout(timer);
    }, [query, shopId]);

    const label = (c: Customer) => `${c.name || '-'} · ${c.phone}`;

    const pick = (value: string, field: 'name' | 'phone') => {
        const hit = results.find((c) => label(c) === value);
        if (hit) {
            onChange({ name: hit.name || '', phone: hit.phone });
            setQuery('');
            return;
        }
        setQuery(value);
        onChange(field === 'name' ? { name: value, phone } : { name, phone: value });
    };

    return (
        <>
            <datalist id={listId}>
                {results.map((c) => (
                    <option key={c.id} value={label(c)}>
                        {t('cart.visits', { count: c.visitCount })}
                    </option>
                ))}
            </datalist>
            <Input
                label={t('cart.customerName')}
                leftIcon={icons ? <User /> : undefined}
                placeholder={t('cart.enterCustomerName')}
                value={name}
                list={listId}
                autoComplete="off"
                required={required}
                onChange={(e) => pick(e.target.value, 'name')}
            />
            <Input
                label={t('cart.customerPhone')}
                leftIcon={icons ? <Phone /> : undefined}
                type="tel"
                placeholder={t('cart.enterCustomerPhone')}
                value={phone}
                list={listId}
                autoComplete="off"
                required={required}
                onChange={(e) => { pick(e.target.value, 'phone'); setMember(undefined); }}
                onBlur={lookupMember}
            />
            {member && <span className="text-xs font-bold text-ink-secondary">{t('cart.memberPoints', { count: member.balance })}</span>}
            {member === null && name.trim() && (
                <Button variant="outline" size="sm" onClick={registerMember}>{t('cart.registerMember')}</Button>
            )}
        </>
    );
};

export default CustomerAutocomplete;
